import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { todayISO } from "@/lib/utils.helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "present", label: "Present", short: "P", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "absent", label: "Absent", short: "A", color: "bg-red-100 text-red-800 border-red-300" },
  { value: "half_day", label: "Half Day", short: "H", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  { value: "leave", label: "Leave", short: "L", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "weekly_off", label: "Weekly Off", short: "WO", color: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "holiday", label: "Holiday", short: "HO", color: "bg-purple-100 text-purple-800 border-purple-300" },
];
const shortOf = (v) => STATUS_OPTIONS.find((o) => o.value === v)?.short || "-";
const colorOf = (v) => STATUS_OPTIONS.find((o) => o.value === v)?.color || "bg-white";

function getCycleStart(ref) {
  const d = new Date(ref);
  if (d.getDate() >= 24) return new Date(d.getFullYear(), d.getMonth(), 24);
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 24);
  return prev;
}
function getCycleEnd(startDate) {
  return new Date(startDate.getFullYear(), startDate.getMonth() + 1, 23);
}
function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Attendance() {
  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">Attendance</div>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Attendance</h1>
        <p className="text-slate-600 mt-1">Daily marking and monthly calendar view (24th–23rd cycle).</p>
      </div>
      <Tabs defaultValue="daily">
        <TabsList className="bg-white border border-[#E5E2DC]">
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly Calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4"><Daily /></TabsContent>
        <TabsContent value="monthly" className="mt-4"><Monthly /></TabsContent>
      </Tabs>
    </div>
  );
}

function Daily() {
  const [date, setDate] = useState(todayISO());
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [empRes, attRes] = await Promise.all([
        api.get("/employees"),
        api.get(`/attendance?start=${date}&end=${date}`),
      ]);
      const active = empRes.data.filter((e) => e.status === "active");
      setEmployees(active);
      const map = {};
      for (const a of attRes.data) map[a.employee_id] = { status: a.status, overtime_hours: a.overtime_hours, remarks: a.remarks || "" };
      for (const e of active) if (!map[e.id]) map[e.id] = { status: "present", overtime_hours: 0, remarks: "" };
      setRows(map);
    } catch { toast.error("Could not load attendance"); }
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line

  const update = (id, patch) => setRows((p) => ({ ...p, [id]: { ...p[id], ...patch } }));
  const markAll = (status) => setRows((p) => {
    const next = { ...p };
    for (const e of employees) next[e.id] = { ...(next[e.id] || {}), status };
    return next;
  });
  const save = async () => {
    setSaving(true);
    try {
      const payload = employees.map((e) => ({
        employee_id: e.id, date,
        status: rows[e.id]?.status || "present",
        overtime_hours: Number(rows[e.id]?.overtime_hours || 0),
        remarks: rows[e.id]?.remarks || "",
      }));
      await api.post("/attendance/bulk", payload);
      toast.success("Attendance saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const filtered = employees.filter((e) => !q || e.name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <Card className="border-[#E5E2DC] p-4 flex flex-col md:flex-row md:items-end gap-4 justify-between">
        <div className="flex flex-wrap gap-3 items-end flex-1">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-date-input" className="mt-1" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Search</label>
            <Input placeholder="Search employee..." value={q} onChange={(e) => setQ(e.target.value)} className="mt-1" data-testid="daily-search-input" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => markAll("present")} data-testid="mark-all-present">All Present</Button>
            <Button variant="outline" size="sm" onClick={() => markAll("weekly_off")} data-testid="mark-all-off">All Weekly Off</Button>
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-attendance-button" className="bg-[#111111] hover:bg-[#C59B27] transition-colors text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Attendance"}
        </Button>
      </Card>
      <Card className="border-[#E5E2DC] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FDFDF9]">
              <TableHead className="uppercase text-[10px] font-bold text-slate-500">Employee</TableHead>
              <TableHead className="uppercase text-[10px] font-bold text-slate-500">Status</TableHead>
              <TableHead className="uppercase text-[10px] font-bold text-slate-500">Overtime (hrs)</TableHead>
              <TableHead className="uppercase text-[10px] font-bold text-slate-500">Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">No active employees.</TableCell></TableRow>
            ) : filtered.map((e) => (
              <TableRow key={e.id} className="hover:bg-slate-50" data-testid={`att-row-${e.id}`}>
                <TableCell>
                  <div className="font-medium text-slate-900">{e.name}</div>
                  <div className="text-xs text-slate-500">{e.employee_code || ""}{e.department ? ` · ${e.department}` : ""}</div>
                </TableCell>
                <TableCell>
                  <Select value={rows[e.id]?.status || "present"} onValueChange={(v) => update(e.id, { status: v })}>
                    <SelectTrigger className="w-36" data-testid={`att-status-${e.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Input type="number" min="0" step="0.5" value={rows[e.id]?.overtime_hours ?? 0} onChange={(ev) => update(e.id, { overtime_hours: ev.target.value })} className="w-24" data-testid={`att-ot-${e.id}`} />
                </TableCell>
                <TableCell>
                  <Input value={rows[e.id]?.remarks || ""} onChange={(ev) => update(e.id, { remarks: ev.target.value })} placeholder="Optional" data-testid={`att-remarks-${e.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Monthly() {
  const [ref, setRef] = useState(new Date());
  const cycleStart = useMemo(() => getCycleStart(ref), [ref]);
  const cycleEnd = useMemo(() => getCycleEnd(cycleStart), [cycleStart]);
  const days = useMemo(() => {
    const arr = [];
    const d = new Date(cycleStart);
    while (d <= cycleEnd) { arr.push(new Date(d)); d.setDate(d.getDate() + 1); }
    return arr;
  }, [cycleStart, cycleEnd]);

  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [empSel, setEmpSel] = useState("all");
  const [att, setAtt] = useState({}); // key `${empId}_${date}` -> record

  const load = async () => {
    const [empRes, attRes] = await Promise.all([
      api.get("/employees"),
      api.get(`/attendance?start=${iso(cycleStart)}&end=${iso(cycleEnd)}`),
    ]);
    setEmployees(empRes.data.filter((e) => e.status === "active"));
    const map = {};
    for (const a of attRes.data) map[`${a.employee_id}_${a.date}`] = a;
    setAtt(map);
  };
  useEffect(() => { load(); }, [cycleStart.getTime(), cycleEnd.getTime()]); // eslint-disable-line

  const cycleStr = `${cycleStart.toLocaleString("en-IN", { month: "short", day: "2-digit" })} - ${cycleEnd.toLocaleString("en-IN", { month: "short", day: "2-digit", year: "numeric" })}`;

  const cycleClick = (delta) => setRef(new Date(cycleStart.getFullYear(), cycleStart.getMonth() + delta, 24));

  const cellChange = async (empId, dateStr, currentStatus) => {
    const order = STATUS_OPTIONS.map((s) => s.value);
    const idx = order.indexOf(currentStatus || "present");
    const next = order[(idx + 1) % order.length];
    try {
      await api.post("/attendance", { employee_id: empId, date: dateStr, status: next, overtime_hours: 0, remarks: "" });
      setAtt((p) => ({ ...p, [`${empId}_${dateStr}`]: { employee_id: empId, date: dateStr, status: next } }));
    } catch { toast.error("Update failed"); }
  };

  const filtered = employees.filter((e) => (empSel === "all" || e.id === empSel) && (!q || e.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-4">
      <Card className="border-[#E5E2DC] p-4 flex flex-col md:flex-row md:items-end gap-3 justify-between">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => cycleClick(-1)} data-testid="cycle-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="px-3 py-2 border border-[#E5E2DC] rounded-md text-sm font-medium bg-white min-w-[220px] text-center" data-testid="cycle-label">{cycleStr}</div>
            <Button variant="outline" size="icon" onClick={() => cycleClick(1)} data-testid="cycle-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="min-w-[160px]">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Employee</label>
            <Select value={empSel} onValueChange={setEmpSel}>
              <SelectTrigger className="mt-1" data-testid="monthly-emp-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Search</label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter names..." className="mt-1" data-testid="monthly-search" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {STATUS_OPTIONS.map((o) => (
            <span key={o.value} className={`px-2 py-1 rounded border ${o.color}`}>{o.short} — {o.label}</span>
          ))}
        </div>
      </Card>

      <Card className="border-[#E5E2DC] overflow-x-auto">
        <div className="min-w-max">
          <table className="text-xs">
            <thead>
              <tr className="bg-[#FDFDF9]">
                <th className="sticky left-0 bg-[#FDFDF9] p-2 text-left uppercase tracking-wider text-[10px] font-bold text-slate-500 min-w-[180px] border-b border-r border-[#E5E2DC]">Employee</th>
                {days.map((d) => (
                  <th key={iso(d)} className="p-1 border-b border-[#E5E2DC] text-center min-w-[36px]">
                    <div className="text-[9px] uppercase text-slate-400">{d.toLocaleString("en-IN", { weekday: "short" }).slice(0,1)}</div>
                    <div className="text-slate-700 font-semibold">{d.getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={days.length + 1} className="p-6 text-center text-slate-400">No employees</td></tr>
              ) : filtered.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50" data-testid={`monthly-row-${e.id}`}>
                  <td className="sticky left-0 bg-white p-2 border-r border-b border-[#E5E2DC] font-medium text-slate-900 whitespace-nowrap">{e.name}</td>
                  {days.map((d) => {
                    const key = `${e.id}_${iso(d)}`;
                    const rec = att[key];
                    const status = rec?.status;
                    return (
                      <td key={key} className="border-b border-[#E5E2DC] p-0.5 text-center">
                        <button
                          onClick={() => cellChange(e.id, iso(d), status)}
                          className={`w-9 h-9 rounded border text-[10px] font-bold transition-colors ${status ? colorOf(status) : "bg-white border-[#E5E2DC] text-slate-300 hover:bg-slate-50"}`}
                          title={status || "Click to mark"}
                          data-testid={`cell-${e.id}-${iso(d)}`}
                        >
                          {status ? shortOf(status) : "·"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="text-xs text-slate-500">Tip: Click a cell to cycle through statuses (Present → Absent → Half Day → Leave → Weekly Off → Holiday).</div>
    </div>
  );
}
